import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AppSummaryCards } from '@/components/admin/app-dashboard/AppSummaryCards';
import { AppCharts } from '@/components/admin/app-dashboard/AppCharts';
import { UserTable } from '@/components/admin/app-dashboard/UserTable';
import { IssuesSummaryCard } from '@/components/admin/app-dashboard/IssuesSummaryCard';
import { IssuesByRoomChart } from '@/components/admin/app-dashboard/IssuesByRoomChart';
import { GeographicBreakdownTable } from '@/components/admin/app-dashboard/GeographicBreakdownTable';
import { WellnessAnalyticsCard } from '@/components/admin/app-dashboard/WellnessAnalyticsCard';
import { VisionAssessmentCard } from '@/components/admin/app-dashboard/VisionAssessmentCard';
import { EngagementMetricsCard } from '@/components/admin/app-dashboard/EngagementMetricsCard';
import { CommunityStatsCard } from '@/components/admin/app-dashboard/CommunityStatsCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

const AppDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch summary counts via RPC
  const { data: summaryData, isLoading: summaryLoading, error: summaryError } = useQuery({
    queryKey: ['app-dashboard-summary'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_dashboard_summary');
      if (error) throw error;
      return data as {
        totalUsers: number;
        totalCircles: number;
        roomAssessments: number;
        drivingAssessments: number;
        tugTests: number;
        thriveScores: number;
        focusOlderAdults: number;
        adlAssessments: number;
        iadlAssessments: number;
        visionAssessments: number;
        wellnessVitals: number;
        medications: number;
      };
    },
  });

  // Fetch user growth via RPC
  const { data: userGrowth } = useQuery({
    queryKey: ['app-dashboard-user-growth'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_user_growth');
      if (error) throw error;
      return (data as Array<{ month: string; users: number }>)?.reverse() || [];
    },
  });

  // Fetch circle roles via RPC
  const { data: circleRoles } = useQuery({
    queryKey: ['app-dashboard-circle-roles'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_circle_roles');
      if (error) throw error;
      return data as Array<{ name: string; value: number }> || [];
    },
  });

  // Fetch room types via RPC
  const { data: roomTypes } = useQuery({
    queryKey: ['app-dashboard-room-types'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_room_types');
      if (error) throw error;
      return data as Array<{ name: string; value: number }> || [];
    },
  });

  // Fetch driving results via RPC
  const { data: drivingResults } = useQuery({
    queryKey: ['app-dashboard-driving-results'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_driving_results');
      if (error) throw error;
      return data as Array<{ name: string; value: number }> || [];
    },
  });

  // Fetch issues by severity via RPC
  const { data: issuesBySeverity, isLoading: issuesSeverityLoading } = useQuery({
    queryKey: ['app-dashboard-issues-severity'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_issues_by_severity');
      if (error) throw error;
      return data as { high: number; medium: number; low: number; total: number };
    },
  });

  // Fetch issues by room type (detailed) via RPC
  const { data: issuesByRoomType, isLoading: issuesRoomLoading } = useQuery({
    queryKey: ['app-dashboard-issues-room-type'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_issues_by_room_type_detailed');
      if (error) throw error;
      return data as Array<{
        roomType: string;
        roomCount: number;
        high: number;
        medium: number;
        low: number;
        total: number;
      }>;
    },
  });

  // Fetch assessments by geography via RPC
  const { data: geographyData, isLoading: geographyLoading } = useQuery({
    queryKey: ['app-dashboard-geography'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_assessments_by_geography');
      if (error) throw error;
      return data as Array<{
        state: string;
        zipCode: string;
        roomCount: number;
        totalIssues: number;
        high: number;
        medium: number;
        low: number;
      }>;
    },
  });

  // Fetch vital signs summary via RPC
  const { data: vitalSignsData, isLoading: vitalSignsLoading } = useQuery({
    queryKey: ['app-dashboard-vital-signs'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_vital_signs_summary');
      if (error) throw error;
      return data as {
        total: number;
        byType: Array<{ name: string; value: number }> | null;
      };
    },
  });

  // Fetch mood distribution via RPC
  const { data: moodData, isLoading: moodLoading } = useQuery({
    queryKey: ['app-dashboard-mood'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_mood_distribution');
      if (error) throw error;
      return data as Array<{ name: string; value: number }> | null;
    },
  });

  // Fetch sleep quality distribution via RPC
  const { data: sleepData, isLoading: sleepLoading } = useQuery({
    queryKey: ['app-dashboard-sleep'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_sleep_quality_distribution');
      if (error) throw error;
      return data as Array<{ name: string; value: number }> | null;
    },
  });

  // Fetch vision assessment summary via RPC
  const { data: visionData, isLoading: visionLoading } = useQuery({
    queryKey: ['app-dashboard-vision'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_vision_assessment_summary');
      if (error) throw error;
      return data as {
        total: number;
        avgLeftEyeScore: number | null;
        avgRightEyeScore: number | null;
        avgColorVisionScore: number | null;
        avgContrastScore: number | null;
        macularHealthDistribution: Array<{ name: string; value: number }> | null;
      };
    },
  });

  // Fetch ThrivePoints summary via RPC
  const { data: thrivePointsData, isLoading: thrivePointsLoading } = useQuery({
    queryKey: ['app-dashboard-thrive-points'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_thrive_points_summary');
      if (error) throw error;
      return data as {
        totalPoints: number;
        totalTransactions: number;
        byComponent: Array<{ name: string; value: number }> | null;
      };
    },
  });

  // Fetch streak summary via RPC
  const { data: streakData, isLoading: streakLoading } = useQuery({
    queryKey: ['app-dashboard-streaks'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_streak_summary');
      if (error) throw error;
      return data as {
        totalStreaks: number;
        avgCurrentStreak: number | null;
        maxLongestStreak: number | null;
        byType: Array<{ name: string; value: number; avgStreak: number }> | null;
      };
    },
  });

  // Fetch community stats via RPC
  const { data: communityData, isLoading: communityLoading } = useQuery({
    queryKey: ['app-dashboard-community'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_community_stats');
      if (error) throw error;
      return data as {
        totalCommunities: number;
        totalMembers: number;
        totalPosts: number;
        totalCirclePosts: number;
        totalReactions: number;
        totalBookmarks: number;
        totalComments: number;
      };
    },
  });

  const wellnessLoading = vitalSignsLoading || moodLoading || sleepLoading;
  const engagementLoading = thrivePointsLoading || streakLoading;

  if (summaryError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">SeniorThrive App Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Real-time analytics for the SeniorThrive application
          </p>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load dashboard data: {(summaryError as Error).message}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">SeniorThrive App Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Real-time analytics for the SeniorThrive application
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Summary Cards */}
          <AppSummaryCards
            data={summaryData}
            isLoading={summaryLoading}
          />

          {/* Issues Overview */}
          <IssuesSummaryCard
            data={issuesBySeverity || null}
            isLoading={issuesSeverityLoading}
          />

          {/* Issues by Room Type */}
          <IssuesByRoomChart
            data={issuesByRoomType || null}
            isLoading={issuesRoomLoading}
          />

          {/* Geographic Breakdown */}
          <GeographicBreakdownTable
            data={geographyData || null}
            isLoading={geographyLoading}
          />

          {/* Wellness Analytics */}
          <WellnessAnalyticsCard
            vitalSigns={vitalSignsData || null}
            moodDistribution={moodData || null}
            sleepQuality={sleepData || null}
            isLoading={wellnessLoading}
          />

          {/* Vision Assessment Insights */}
          <VisionAssessmentCard
            data={visionData || null}
            isLoading={visionLoading}
          />

          {/* Engagement Metrics */}
          <EngagementMetricsCard
            thrivePoints={thrivePointsData || null}
            streaks={streakData || null}
            isLoading={engagementLoading}
          />

          {/* Community Stats */}
          <CommunityStatsCard
            data={communityData || null}
            isLoading={communityLoading}
          />

          {/* Charts */}
          <AppCharts
            userGrowth={userGrowth || []}
            circleRoles={circleRoles || []}
            roomTypes={roomTypes || []}
            drivingResults={drivingResults || []}
          />
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <UserTable />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AppDashboard;